// PARAQTA — Бүгін v2 (Опус 4.7 дизайн).
//
// loadTodayV2() — fetch /api/today/v2, populate [data-field], apply
// [data-visibility] rules (free/jandos/family_member), wire Pomodoro
// state machine + Tab свайп.
//
// Старая loadDashboard() — legacy guard-пен өтеді (DOM элементтері
// жоқ болса — осы модуль шақырылады).

import { api } from '../../core/api.js';
import { show } from '../../core/router.js';
import { openBookPage } from '../book-page/index.js';
import { toast } from '../../utils/toast.js';

// ── State (module-private) ─────────────────────────────────────
const POMO_TOTAL = 25 * 60;          // 25 минут
const STREAK_MIN = 5 * 60;            // 5 минут — streak шарты
const UPDATE_INTERVAL = 30 * 1000;    // 30s — backend session update

const state = {
  data: null,
  activeBookIdx: 0,
  pomodoro: {
    sessionId: null,
    bookId: null,
    seconds: 0,                       // өткен секундтар (0..1500)
    isRunning: false,
    tickId: null,
    syncId: null,
    startedAt: null,
  },
  currentPage: 0,
};

// ── Utilities ──────────────────────────────────────────────────
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const root = () => document.getElementById('pg-dash');

function setField(name, value) {
  const el = $(`[data-field="${name}"]`, root());
  if (el) el.textContent = value;
}

function setFieldHTML(name, html) {
  const el = $(`[data-field="${name}"]`, root());
  if (el) el.innerHTML = html;
}

function fmtPomo(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

// Активі (0..1) → var(--ac) tinted color-mix — heatmap квадрат түсі (theme-aware).
function activityColor(activity) {
  // Stage 6.8.2 founder bug: 4% empty dark mode-та көрінбейді — 12%.
  if (activity <= 0) return 'color-mix(in srgb, var(--ink) 12%, transparent)';
  return `color-mix(in srgb, var(--ac) ${Math.round(activity * 100)}%, transparent)`;
}

// ── Visibility (subscription_status-қа сай) ────────────────────
function applyVisibility(data) {
  const sub = data?.user?.subscription_status || 'free';
  const isAdmin = !!data?.user?.is_admin;
  const hasMarathon = !!data?.active_marathon;

  $$('[data-visibility]', root()).forEach(el => {
    const rule = el.dataset.visibility;
    let visible = true;
    if (rule === 'free-only') visible = (sub === 'free');
    else if (rule === 'premium-only') visible = (sub === 'jandos' || sub === 'family_member');
    else if (rule === 'family-member-only') visible = (sub === 'family_member');
    else if (rule === 'admin-only') visible = isAdmin;
    else if (rule === 'if-active') visible = hasMarathon;
    el.style.display = visible ? '' : 'none';
  });
}

// ── Render ──────────────────────────────────────────────────────
function renderUser(data) {
  const u = data.user || {};
  setField('user-initials', u.initials || '—');
  setField('user-name', u.name || 'Оқырман');
  setField('today-date', data.today_date_formatted || '');
  setField('books-finished-year', `${u.books_finished_year ?? 0} кітап аяқтадың осы жылы`);
  setField('streak-days', u.streak ?? 0);

  // Admin shield button — admin-ге ғана display: flex
  const shield = $('[data-section="admin-shield"]', root());
  if (shield) shield.style.display = u.is_admin ? 'flex' : 'none';
}

// Шам-стрейк герой — стрейкті бас кейіпкер ету (сәлемдесудің астында).
// data.user.streak (= бот users.streak) — беттің қалған жерімен бірдей дерек.
// Additive: бар секцияларға тимейді, тек өз контейнерін толтырады.
function renderStreakHero(data) {
  const el = $('[data-field="streak-hero"]', root());
  if (!el) return;
  const streak = Number((data.user || {}).streak || 0);
  // Апталық шамдар: соңғы 7 күн heatmap-тан (болса), әйтпесе стрейкке қарай.
  const hm = Array.isArray(data.streak_heatmap) ? data.streak_heatmap.slice(-7) : [];
  const dim = 'opacity:.22;filter:grayscale(1);';
  let dots;
  if (hm.length) {
    dots = hm.map(d => {
      const lit = d && (d.pages > 0 || d.read || d.active || d.count > 0);
      return `<span style="font-size:17px;line-height:1;${lit ? '' : dim}">🕯</span>`;
    }).join('');
  } else {
    const lit = Math.min(7, streak);
    dots = Array.from({ length: 7 }, (_, i) =>
      `<span style="font-size:17px;line-height:1;${i < lit ? '' : dim}">🕯</span>`).join('');
  }
  const sub = streak > 0 ? 'күн — шамың жанып тұр' : 'бүгін оқып, шамыңды жақ';
  const next = streak > 0 ? `бүгін оқысаң — ${streak + 1} болады` : '';
  el.innerHTML = `
    <div style="background:var(--card);border:.5px solid color-mix(in srgb, var(--ac) 14%, transparent);border-radius:22px;box-shadow:var(--shadow);padding:18px 16px 16px;text-align:center;">
      <svg width="46" height="60" viewBox="0 0 78 110" aria-hidden="true" style="display:block;margin:0 auto;">
        <defs>
          <linearGradient id="pqShWax" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#D8B987"/><stop offset="50%" stop-color="#C9A96E"/><stop offset="100%" stop-color="#8B5E34"/></linearGradient>
          <radialGradient id="pqShFl" cx="0.5" cy="0.6" r="0.6"><stop offset="0%" stop-color="#FFF8E0"/><stop offset="40%" stop-color="#FFE599"/><stop offset="80%" stop-color="#E9A949"/><stop offset="100%" stop-color="#8B5E34" stop-opacity="0.6"/></radialGradient>
        </defs>
        <rect x="26" y="40" width="26" height="58" rx="3" fill="url(#pqShWax)"/>
        <line x1="39" y1="40" x2="39" y2="30" stroke="var(--ink)" stroke-width="1.4"/>
        <ellipse cx="39" cy="21" rx="9" ry="14" fill="url(#pqShFl)"/>
        <ellipse cx="39" cy="23" rx="4" ry="8" fill="#FFF8E0"/>
      </svg>
      <div style="font-family:Georgia,serif;font-size:52px;font-weight:600;color:var(--ink);line-height:1;margin-top:8px;">${streak}</div>
      <div style="font-size:13.5px;color:var(--ink3);margin-top:3px;">${sub}</div>
      <div style="display:flex;justify-content:center;gap:8px;margin-top:13px;">${dots}</div>
      ${next ? `<div style="font-size:12px;color:var(--ink4);margin-top:11px;">${next}</div>` : ''}
      ${Number(data.freeze_balance) > 0 ? `<div style="font-size:11.5px;color:var(--ac);margin-top:11px;display:inline-flex;align-items:center;gap:5px;background:color-mix(in srgb,var(--ac) 9%,transparent);padding:5px 11px;border-radius:999px;">🛡 ${Number(data.freeze_balance)} мұздатуың бар — 1 күн өткізсең қорғайды</div>` : ''}
    </div>`;
  el.style.display = 'block';
}

function renderReadingNow(data) {
  const list = Array.isArray(data.reading_now) ? data.reading_now : [];
  const tabsEl = $('[data-field="reading-tabs"]', root());
  if (!tabsEl) return;

  // Tabs: рендер + "+" батырма сақтау
  const plusBtn = $('button[data-action="add-book"]', tabsEl);
  tabsEl.innerHTML = '';

  if (list.length === 0) {
    // Бос state — "+" батырма ғана
    if (plusBtn) tabsEl.appendChild(plusBtn);
    setField('book-title', 'Кітап қос');
    setField('book-author-pages', 'Кітапханадан кітап таңда');
    setField('book-cover-title', '+');
    setField('book-current-page', '0');
    setField('book-total-pages', '0');
    setField('book-progress-pct', '0%');
    const bar = $('[data-field="book-progress-bar"]', root());
    if (bar) bar.style.width = '0%';
    state.activeBookIdx = -1;
    return;
  }

  list.forEach((b, i) => {
    const isActive = (i === state.activeBookIdx);
    const btn = document.createElement('button');
    btn.dataset.tab = isActive ? 'active' : 'inactive';
    btn.dataset.bookId = b.book_id;
    btn.dataset.action = 'reading-tab-click';
    btn.dataset.idx = i;
    const baseStyle = 'flex-shrink: 0; padding: 7px 12px; border-radius: 999px; font-size: 11px; cursor: pointer; white-space: nowrap;';
    if (isActive) {
      btn.style.cssText = baseStyle + 'background: color-mix(in srgb, var(--gold) 18%, transparent); border: 0.5px solid color-mix(in srgb, var(--gold) 40%, transparent); color: var(--gold); font-weight: 500;';
      btn.textContent = `📖 ${b.title}`;
    } else {
      // Stage 6.8.4: tabs inside dark gradient card — hardcoded cream
      // (var(--bg) flips to dark in dark mode → invisible on dark card).
      btn.style.cssText = baseStyle + 'background: color-mix(in srgb, #F7F2EA 6%, transparent); border: 0.5px solid color-mix(in srgb, #F7F2EA 10%, transparent); color: color-mix(in srgb, #F7F2EA 65%, transparent);';
      btn.textContent = b.title;
    }
    tabsEl.appendChild(btn);
  });
  if (plusBtn) tabsEl.appendChild(plusBtn);

  // Активті кітаптың details
  const active = list[state.activeBookIdx] || list[0];
  if (!active) return;

  setField('book-title', active.title || '—');
  setField('book-author-pages', `${active.author || ''} · ${active.total_pages || 0} бет`);
  setField('book-cover-title', active.title || '');
  setField('book-current-page', active.current_page || 0);
  setField('book-total-pages', active.total_pages || 0);
  setField('book-progress-pct', `${active.progress_pct ?? 0}%`);
  setField('current-page-display', active.current_page || 0);

  state.currentPage = Number(active.current_page) || 0;
  state.pomodoro.bookId = active.book_id;

  const bar = $('[data-field="book-progress-bar"]', root());
  if (bar) bar.style.width = `${active.progress_pct ?? 0}%`;

  // Cover image (бар болса)
  const cover = $('[data-field="book-cover"]', root());
  if (cover) {
    const innerText = cover.querySelector('[data-field="book-cover-title"]');
    if (active.cover_url) {
      cover.style.background = `url(${active.cover_url}) center/cover no-repeat`;
      if (innerText) innerText.style.display = 'none';
    } else {
      cover.style.background = 'linear-gradient(135deg, #8B5E34 0%, #6B4423 100%)';
      if (innerText) innerText.style.display = '';
    }
  }
}

function renderStats(data) {
  setField('today-minutes', data.today_minutes ?? 0);
  setField('week-time', data.week_time_formatted || '0 мин');
}

function renderHeatmap(data) {
  const grid = $('[data-field="heatmap-grid"]', root());
  if (!grid) return;
  const days = Array.isArray(data.streak_heatmap) ? data.streak_heatmap : [];
  grid.innerHTML = '';
  days.forEach(d => {
    const sq = document.createElement('div');
    const shadow = d.is_today ? 'box-shadow: 0 0 0 1.5px #C9A96E;' : '';
    sq.style.cssText = `aspect-ratio: 1; background: ${activityColor(d.activity || 0)}; border-radius: 2px; ${shadow}`;
    sq.title = `${d.date}: ${d.pages} бет`;
    sq.dataset.dayPages = d.pages;
    sq.dataset.activityLevel = Math.round((d.activity || 0) * 100);
    grid.appendChild(sq);
  });
}

function renderFamily(data) {
  const fam = data.family;
  if (!fam) return;  // visibility rule-мен жасырылады

  setField('family-members-count', `${fam.members_count ?? 0} адам`);

  const avatars = $('[data-field="family-avatars"]', root());
  if (avatars) {
    avatars.innerHTML = '';
    (fam.members || []).slice(0, 5).forEach((m, i) => {
      const av = document.createElement('div');
      const ml = i === 0 ? '' : 'margin-left: -10px;';
      av.style.cssText = `width: 36px; height: 36px; border-radius: 50%; background: ${m.color}; color: var(--bg); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 500; border: 2.5px solid #F7F2EA; ${ml}`;
      av.textContent = m.initials || '?';
      avatars.appendChild(av);
    });
    // Толып кетсе — "+N" placeholder
    if ((fam.members_count || 0) > 5) {
      const more = document.createElement('div');
      more.style.cssText = 'width: 36px; height: 36px; border-radius: 50%; background: color-mix(in srgb, var(--ac) 15%, transparent); color: var(--ac); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 500; border: 2.5px solid #F7F2EA; margin-left: -10px;';
      more.textContent = `+${fam.members_count - 5}`;
      avatars.appendChild(more);
    }
  }

  const summary = fam.leader
    ? `Бұл аптада бірге <span style="color: var(--ac); font-weight: 500;">${fam.weekly_pages || 0} бет</span> парақтадыңдар. ${esc(fam.leader.name)} ${fam.leader.ahead_by_pages || 0} беттен алда!`
    : `Бұл аптада бірге <span style="color: var(--ac); font-weight: 500;">${fam.weekly_pages || 0} бет</span> парақтадыңдар.`;
  setFieldHTML('family-summary', summary);
}

// Initials үшін helper (Top-3 аватарлар)
function initials(u) {
  const f = (u.first_name || u.name || '').trim();
  const l = (u.last_name || '').trim();
  const ch = (s) => s ? s[0].toUpperCase() : '';
  const r = ch(f) + ch(l);
  return r || (u.username ? u.username[0].toUpperCase() : '•');
}

// Stage 7.2.2: Бүгінгі сессиялар widget.
// Founder идеясы: бір күнде бірнеше сессия (таңғы / кешкі) болуы мүмкін —
// әрқайсысында қанша бет / қанша уақыт оқыды.
function renderTodaySessions(data) {
  const wrap = $('[data-field="today-sessions"]', root());
  if (!wrap) return;
  const sess = Array.isArray(data.today_sessions) ? data.today_sessions : [];
  if (!sess.length) {
    // Бүгін әлі сессия жасалмаған → widget жасырын (Бүгін бетін шамалап тұр).
    wrap.style.display = 'none';
    wrap.innerHTML = '';
    return;
  }
  wrap.style.display = '';

  const totalPages = Number(data.today_sessions_total_pages || 0);
  const totalSecs = Number(data.today_sessions_total_seconds || 0);
  const totalMin = Math.round(totalSecs / 60);

  const rows = sess.map(s => {
    const hh = fmtSessionTime(s.started_at);
    const pages = Number(s.pages || 0);
    const mins = Number(s.minutes || 0);
    const sp = Number(s.start_page || 0);
    const ep = Number(s.end_page || 0);
    const rangeText = (sp && ep) ? (sp + '→' + ep + '-бет') : '';
    const titleText = s.book_title || '—';
    const finishedBadge = s.finished_book
      ? '<span class="ts-finished">✓ аяқтадым</span>'
      : '';
    return `
      <div class="ts-row">
        <div class="ts-time">
          <div class="ts-flame">🕯</div>
          <div class="ts-hh">${hh}</div>
        </div>
        <div class="ts-info">
          <div class="ts-book">${esc(titleText)} ${finishedBadge}</div>
          <div class="ts-meta">
            <b>${pages}</b> бет
            <span class="ts-dot">·</span>
            <b>${mins}</b> мин
            ${rangeText ? `<span class="ts-dot">·</span><span class="ts-range">${rangeText}</span>` : ''}
          </div>
        </div>
      </div>`;
  }).join('');

  wrap.innerHTML = `
    <section class="g7-sec-head">
      <div class="g7-sec-title">Бүгін оқыдың</div>
      <div class="ts-total">
        <b>${totalPages}</b> бет
        <span class="ts-dot">·</span>
        <b>${totalMin}</b> мин
        <span class="ts-dot">·</span>
        ${sess.length} сессия
      </div>
    </section>
    <div class="ts-card">${rows}</div>
  `;
}

function fmtSessionTime(iso) {
  if (!iso) return '—';
  // SQLite datetime('now') UTC қайтарады, ал date('now','localtime')
  // server timezone-да. Барлық сессиялар бір ғана timezone-да —
  // start uri-ден HH:MM аламыз.
  // Format mysql/sqlite: "2026-05-23 00:56:35" немесе "2026-05-23T00:56:35"
  const m = String(iso).match(/(\d{2}):(\d{2})/);
  return m ? (m[1] + ':' + m[2]) : '—';
}

function renderRankWidget(data) {
  const root_ = $('[data-field="rank-widget"]', root());
  if (!root_) return;
  const rank = data.my_rank;
  const myPages = data.my_pages_month ?? 0;
  const top3 = Array.isArray(data.top_3) ? data.top_3 : [];

  root_.innerHTML = `
    <div class="g7-sec-head">
      <div class="g7-sec-title">Айлық рейтиңтегі орның</div>
      <button class="g7-sec-more" data-action="navigate" data-target="leaders">
        Толық рейтиң <i class="ti ti-chevron-right" style="font-size: 11px;"></i>
      </button>
    </div>
    <div class="rw-card" data-action="navigate" data-target="leaders">
      <div class="rw-me">
        <div class="rw-pos">${rank ? '#' + rank : '—'}</div>
        <div class="rw-info">
          <div class="rw-name">Сенің орның</div>
          <div class="rw-sub">${rank ? myPages + ' бет · бұл ай' : 'бұл айда оқып, рейтиңге кір'}</div>
        </div>
      </div>
      ${top3.length ? `
      <div class="rw-divider"></div>
      <div class="rw-top3">
        ${top3.map((u, i) => `
          <div class="rw-row">
            <div class="rw-rank rw-rank-${i+1}">${i+1}</div>
            <div class="rw-av">${esc(initials(u))}</div>
            <div class="rw-row-name">${esc(u.name || ((u.first_name||'') + ' ' + (u.last_name||'')).trim() || 'Оқырман')}</div>
            <div class="rw-row-streak"><b>${u.pages || 0}</b> бет</div>
          </div>`).join('')}
      </div>` : ''}
    </div>
  `;
}

function renderGroups(data) {
  const groups = Array.isArray(data.groups) ? data.groups : [];

  const list = $('[data-field="groups-list"]', root());
  if (!list) return;

  list.innerHTML = '';

  // Stage 7.1: горизонтал scroll — топ-карталар + соңында "Топ табу".
  groups.forEach(g => {
    const card = document.createElement('div');
    card.className = 'group-card';
    card.dataset.groupId = g.group_id;
    card.dataset.action = 'open-group';
    card.innerHTML = `
      <div class="gc-cover" style="background: ${g.book_cover_gradient || 'linear-gradient(135deg, #8B5E34 0%, #6B4423 100%)'};">
        <div class="gc-cover-text">${esc(g.book_cover_text || g.name)}</div>
      </div>
      <div class="gc-name">${esc(g.name)}</div>
      <div class="gc-meta">
        <i class="ti ti-users"></i>
        <span>${g.members_count || 0} мүше</span>
      </div>
      <div class="gc-bar"><div class="gc-bar-fill" style="width: ${g.progress_pct || 0}%;"></div></div>
    `;
    list.appendChild(card);
  });

  // Stage 7.4: "Топ табу" → groups page
  const add = document.createElement('div');
  add.className = 'group-card group-card-add';
  add.dataset.action = 'navigate';
  add.dataset.target = 'groups';
  add.innerHTML = `
    <div class="gc-add-icon"><i class="ti ti-plus"></i></div>
    <div class="gc-add-label">Топ табу</div>
  `;
  list.appendChild(add);
}

function renderQuote(data) {
  const q = data.daily_quote || {};
  setField('quote-text', `«${q.text || ''}»`);
  setField('quote-author', q.author || '');
  setField('quote-book', q.book ? `«${q.book}»` : '');
}

function renderFeatured(data) {
  const f = data.featured_book;
  const section = $('[data-section="featured"]', root());
  // Featured null болса — секцияны бүтіндей жасыру.
  if (!f) {
    if (section) section.style.display = 'none';
    return;
  }
  if (section) section.style.display = '';

  // Header — "Сәуір айының кітабы" (бэкенд динамикалық қайтарады)
  setField('featured-header', f.header || 'Айдың кітабы');
  setField('featured-title', f.title || '—');
  setField('featured-cover-title', f.cover_text || f.title || '');

  // Author + year: "Дулат Исабеков · 1976" (year болса)
  const ayParts = [];
  if (f.author) ayParts.push(f.author);
  if (f.year) ayParts.push(String(f.year));
  setField('featured-author-year', ayParts.join(' · ') || '—');

  // Rating + stars — қазір ҚОЛДАНЫЛМАЙДЫ (reviews-те rating жоқ).
  // Stats строкасы: "N оқыды · M пікір"
  const ratingEl = $('[data-field="featured-rating"]', root());
  const starsEl = $('[data-field="featured-stars"]', root());
  if (f.rating != null && starsEl && ratingEl) {
    ratingEl.style.display = '';
    starsEl.style.display = 'flex';
    ratingEl.textContent = f.rating.toFixed(1);
    const r = Math.max(0, Math.min(5, Math.round(f.rating)));
    starsEl.innerHTML = '';
    for (let i = 1; i <= 5; i++) {
      const star = document.createElement('i');
      star.className = 'ti ti-star';
      star.style.opacity = (i <= r) ? '1' : '0.25';
      starsEl.appendChild(star);
    }
  } else {
    if (ratingEl) ratingEl.style.display = 'none';
    if (starsEl) starsEl.style.display = 'none';
  }

  // Stats: "224 оқыды · 46 пікір"
  const stats = [];
  if (f.readers_count) stats.push(`${f.readers_count} оқыды`);
  if (f.reviews_count) stats.push(`${f.reviews_count} пікір`);
  setField('featured-stats', stats.join(' · ') || '—');

  // Cover — реалды URL немесе gradient fallback
  const cover = $('[data-field="featured-cover"]', root());
  if (cover) {
    const innerText = cover.querySelector('[data-field="featured-cover-title"]');
    if (f.cover_url) {
      cover.style.background = `url(${f.cover_url}) center/cover no-repeat`;
      if (innerText) innerText.style.display = 'none';
    } else {
      cover.style.background = 'linear-gradient(135deg, #1A0A2E 0%, #4A1E7C 100%)';
      if (innerText) innerText.style.display = '';
    }
  }

  // "Кітапты ашу" батырманы — slug болса enable, жоқ болса disable
  const openBtn = $('[data-action="open-featured-book"]', root());
  if (openBtn) {
    if (f.slug) {
      openBtn.style.opacity = '1';
      openBtn.style.pointerEvents = '';
      openBtn.dataset.slug = f.slug;
      // Бар text-ті қалпына келтіру (жолы dataset.disabled бар болса)
      delete openBtn.dataset.disabled;
    } else {
      openBtn.style.opacity = '0.5';
      openBtn.style.pointerEvents = 'none';
      openBtn.dataset.disabled = '1';
      // Visual hint
      openBtn.innerHTML = '<i class="ti ti-clock" style="font-size: 13px;"></i> Кітап беті жақын арада';
    }
  }
}

// Марафон фазасы (date-only, жергілікті): before_reg → reg_open → live → ended.
function marathonPhaseLocal(m) {
  const d = new Date();
  const t = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (m.reg_open_date && t < m.reg_open_date) return 'before_reg';
  if (m.start_date && t < m.start_date) return 'reg_open';
  if (m.end_date && t <= m.end_date) return 'live';
  if (m.end_date && t > m.end_date) return 'ended';
  return 'reg_open';
}

function renderMarathon(data) {
  const m = data.active_marathon;
  if (!m) return;
  setField('marathon-title', m.title || '');
  setField('marathon-info', m.info || '');

  // Батырма мәтіні — фазаға қарай (тіркелу ашылмаса "Тіркелу" демейміз).
  const btn = root()?.querySelector('[data-action="open-marathon"]');
  if (btn) {
    const fee = m.registration_fee?.toLocaleString('kk-KZ') || '—';
    const ph = marathonPhaseLocal(m);
    const label = {
      before_reg: 'Толығырақ',
      reg_open: `Тіркелу — ${fee} ₸`,
      // Кеш тіркелу ашық болса — жүріп жатқанда да қосылуға шақырамыз.
      live: m.reg_late ? `Қосылу — ${fee} ₸` : 'Марафон жүріп жатыр',
      ended: 'Қорытынды',
    }[ph] || `Тіркелу — ${fee} ₸`;
    btn.innerHTML = `${label} <i class="ti ti-arrow-right" style="font-size: 13px;"></i>`;
  }
}

// ── Pomodoro state machine ─────────────────────────────────────
function updatePomoUI() {
  const sec = POMO_TOTAL - state.pomodoro.seconds;
  setField('pomodoro-time', fmtPomo(Math.max(sec, 0)));

  const icon = $('[data-field="pomodoro-icon"]', root());
  if (icon) {
    icon.className = state.pomodoro.isRunning
      ? 'ti ti-player-pause-filled'
      : 'ti ti-player-play-filled';
    icon.style.fontSize = '20px';
  }
}

function pomoTick() {
  state.pomodoro.seconds += 1;
  updatePomoUI();
  if (state.pomodoro.seconds >= POMO_TOTAL) {
    // 25 минут — аяқтау
    pomoEnd(true);
  }
}

async function pomoStart() {
  const bookId = state.pomodoro.bookId;
  if (!bookId) {
    toast('Алдын кітап таңда');
    return;
  }
  try {
    const res = await api('/api/reading/session/start', {
      method: 'POST',
      body: JSON.stringify({ book_id: bookId }),
    });
    state.pomodoro.sessionId = res.session_id;
  } catch (e) {
    console.warn('pomodoro start failed', e);
    toast('Сессия басталмады');
    return;
  }
  state.pomodoro.isRunning = true;
  state.pomodoro.startedAt = Date.now();
  state.pomodoro.tickId = setInterval(pomoTick, 1000);
  // 30s сайын backend-ке seconds sync
  state.pomodoro.syncId = setInterval(() => {
    pomoSync(false);
  }, UPDATE_INTERVAL);
  updatePomoUI();
}

async function pomoSync(includePage) {
  if (!state.pomodoro.sessionId) return;
  try {
    const body = {
      session_id: state.pomodoro.sessionId,
      seconds: state.pomodoro.seconds,
    };
    if (includePage && state.currentPage > 0) body.page = state.currentPage;
    await api('/api/reading/session/update', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (e) {
    console.warn('pomodoro sync failed', e);
  }
}

function pomoPause() {
  state.pomodoro.isRunning = false;
  if (state.pomodoro.tickId) {
    clearInterval(state.pomodoro.tickId);
    state.pomodoro.tickId = null;
  }
  if (state.pomodoro.syncId) {
    clearInterval(state.pomodoro.syncId);
    state.pomodoro.syncId = null;
  }
  pomoSync(true);
  updatePomoUI();
}

async function pomoEnd(autoFinished) {
  if (state.pomodoro.tickId) clearInterval(state.pomodoro.tickId);
  if (state.pomodoro.syncId) clearInterval(state.pomodoro.syncId);
  state.pomodoro.tickId = null;
  state.pomodoro.syncId = null;
  const wasRunning = state.pomodoro.isRunning;
  state.pomodoro.isRunning = false;

  if (state.pomodoro.sessionId) {
    try {
      const res = await api('/api/reading/session/end', {
        method: 'POST',
        body: JSON.stringify({
          session_id: state.pomodoro.sessionId,
          seconds: state.pomodoro.seconds,
          page: state.currentPage || undefined,
        }),
      });
      if (autoFinished) {
        toast(res.streak_eligible
          ? '🎉 25 минут! Streak қосылды.'
          : '✓ Сессия аяқталды.');
      } else if (wasRunning) {
        toast(res.streak_eligible
          ? '✓ Streak қосылды.'
          : `✓ ${Math.floor(state.pomodoro.seconds/60)} мин — streak үшін мин 5 мин`);
      }
    } catch (e) {
      console.warn('pomodoro end failed', e);
    }
  }
  state.pomodoro.sessionId = null;
  state.pomodoro.seconds = 0;
  state.pomodoro.startedAt = null;
  updatePomoUI();
}

// Stage 7.1: Pomodoro UI Бүгін-нен кетті (центр Орб → "Оқу" беті, Stage 7.2).
// Бұл функциялар басқа жерлерден импортталады (router.js → pomodoroStop, events.js)
// — атын ЖОЙМАЙ, ішін noop ете отырып backward compat сақтаймыз.
// Stage 7.2-де бұлар "Оқу" бетінің таймер state machine-іне көшеді.
export function pomodoroToggle() { /* noop — moved to Stage 7.2 (Оқу) */ }
export function pomodoroStop()   { /* noop — moved to Stage 7.2 (Оқу) */ }
export function pageIncrement()  { /* noop — moved to Stage 7.2 (Оқу) */ }
export function pageDecrement()  { /* noop — moved to Stage 7.2 (Оқу) */ }

function recalcProgress() {
  const active = (state.data?.reading_now || [])[state.activeBookIdx];
  if (!active) return;
  const total = active.total_pages || 0;
  const pct = total > 0 ? Math.round(state.currentPage / total * 100) : 0;
  setField('book-progress-pct', `${pct}%`);
  const bar = $('[data-field="book-progress-bar"]', root());
  if (bar) bar.style.width = `${pct}%`;
}

// ── Reading Tab click (свайп) ──────────────────────────────────
export function readingTabClick(idx) {
  const newIdx = Number(idx);
  if (newIdx === state.activeBookIdx) return;
  // Активі кітап ауысқанда — pomodoro pause + reset
  if (state.pomodoro.isRunning || state.pomodoro.sessionId) {
    pomoEnd(false);
  }
  state.activeBookIdx = newIdx;
  if (state.data) renderReadingNow(state.data);
}

// ── Other actions ──────────────────────────────────────────────
export function openFeaturedBook() {
  const f = state.data?.featured_book;
  if (!f) return;
  // Slug міндетті — Round 2 backend slug merge жасайды (_attach_book_slugs)
  if (!f.slug) {
    toast('Кітап беті жақын арада');
    return;
  }
  try {
    openBookPage(f.slug);
  } catch (e) {
    console.warn('openBookPage failed', e);
    toast('Кітап бетіне өту мүмкін болмады');
  }
}

export function openGroupCard(groupId) {
  // Stage 7.4: pendingGroupOpen — groups page бұл топты ашады.
  if (groupId) window._pendingGroupOpen = parseInt(groupId, 10);
  show('groups');
}

export function saveQuote() {
  toast('💾 Дәйексөз сақталды');
}

export function streakDetail() {
  toast('🔥 Streak deталь — жақын арада');
}

export function openFamily() {
  toast('👨‍👩‍👧 Family дәптер — жақын арада');
}

export function openJandos() {
  // Stage 6.7: subscription page — /жан-дос беті.
  show('jandos');
}

export function openMarathon() {
  show('marathon');
}

export function addBook() {
  show('catalog');
}

export function createGroup() {
  show('groups');
}

// Stage 7.5 Session 3 + launch — Айлық есеп banner (Бүгін бетінің жоғарысы)
function renderMonthlyWrappedBanner(data) {
  const el = $('[data-field="monthly-wrapped-banner"]', root());
  if (!el) return;
  const w = data?.monthly_wrapped;
  if (!w || !w.pages) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  el.style.display = '';
  const promoLabel = w.is_promo ? '<span style="font-size:9px;letter-spacing:.8px;color:#fff;background:#3A5C3C;padding:2px 8px;border-radius:999px;font-weight:600;margin-left:6px;">ТЕГІН</span>' : '';
  el.innerHTML = `
    <div data-action="open-wrapped-monthly-latest" style="margin:14px 16px 0;padding:14px 16px;background:linear-gradient(155deg,#FBF7EF 0%,#F1E9D8 100%);border:.5px solid rgba(139,94,52,.22);border-radius:14px;cursor:pointer;display:flex;align-items:center;gap:14px;box-shadow:0 4px 14px rgba(139,94,52,.08);">
      <div style="width:48px;height:48px;flex-shrink:0;position:relative;display:flex;align-items:flex-end;justify-content:center;">
        <div style="position:absolute;top:-2px;left:50%;transform:translateX(-50%);width:48px;height:48px;background:radial-gradient(circle,rgba(233,169,73,.45) 0%,transparent 65%);pointer-events:none;"></div>
        <svg viewBox="0 0 78 104" style="width:100%;height:100%;" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="bnr-wax" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stop-color="#D8B987"/><stop offset="50%" stop-color="#C9A96E"/><stop offset="100%" stop-color="#8B5E34"/>
            </linearGradient>
            <radialGradient id="bnr-flame" cx="0.5" cy="0.6" r="0.6">
              <stop offset="0%" stop-color="#FFF8E0"/><stop offset="40%" stop-color="#FFE599"/>
              <stop offset="80%" stop-color="#E9A949"/><stop offset="100%" stop-color="#8B5E34" stop-opacity="0.6"/>
            </radialGradient>
          </defs>
          <ellipse cx="39" cy="100" rx="18" ry="2.5" fill="#8B5E34" opacity="0.22"/>
          <rect x="26" y="38" width="26" height="60" rx="3" fill="url(#bnr-wax)"/>
          <line x1="39" y1="38" x2="39" y2="28" stroke="#1C1A16" stroke-width="1.4"/>
          <ellipse cx="39" cy="20" rx="9" ry="14" fill="url(#bnr-flame)"/>
          <ellipse cx="39" cy="22" rx="4" ry="8" fill="#FFF8E0"/>
        </svg>
      </div>
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;flex-wrap:wrap;gap:4px;margin-bottom:4px;">
          <div style="font-family:'PT Serif',Georgia,serif;font-size:13.5px;font-weight:700;color:#1C1A16;">${esc(w.month_name)} айына есебің дайын</div>
          ${promoLabel}
        </div>
        <div style="font-size:11.5px;color:rgba(28,26,22,.6);">
          <b style="font-family:'PT Serif',Georgia,serif;color:#8B5E34;font-weight:700;">${w.pages}</b> бет
          ${w.time_text ? ` · ${esc(w.time_text)}` : ''}
          ${w.active_days ? ` · ${w.active_days} күн` : ''}
        </div>
      </div>
      <div style="font-size:18px;color:#8B5E34;flex-shrink:0;">→</div>
    </div>
  `;
}

// Stage 7.6 — Күндік мақсат виджеті (бот daily_str-мен синхрон)
function renderDailyGoal(data) {
  const el = $('[data-field="daily-goal"]', root());
  if (!el) return;
  const g = data?.daily_goal;
  if (!g) { el.style.display = 'none'; el.innerHTML = ''; return; }
  el.style.display = '';

  if (!g.has_goal) {
    // Мақсат жоқ — жұмсақ prompt (кітап бетінде қоюға болады)
    el.innerHTML = `
      <div style="margin:14px 16px 0;padding:13px 16px;background:var(--card);border:.5px dashed color-mix(in srgb,var(--ac) 30%,transparent);border-radius:14px;display:flex;align-items:center;gap:12px;">
        <div style="font-size:20px;">🎯</div>
        <div style="flex:1;font-size:12px;color:color-mix(in srgb,var(--ink) 60%,transparent);font-family:'Cormorant Garamond',serif;font-style:italic;">Күндік мақсат қойылмаған — кітап бетінде орнат</div>
      </div>`;
    return;
  }

  const pct = g.target > 0 ? Math.min(100, Math.round((g.read_today / g.target) * 100)) : 0;
  const done = g.done;
  const barColor = done ? 'linear-gradient(to right,#3A5C3C,#5A7C5C)' : 'linear-gradient(to right,#8B5E34,#C9A96E)';
  const statusLine = done
    ? `<span style="color:#3A5C3C;font-weight:700;">✓ Бүгінгі мақсат орындалды!</span>`
    : `<b style="font-family:'PT Serif',Georgia,serif;color:#8B5E34;font-weight:700;">${g.read_today}</b> / ${g.target} бет · <span style="color:var(--ac);font-weight:600;">${g.remaining} қалды</span>`;

  el.innerHTML = `
    <div style="margin:14px 16px 0;padding:14px 16px;background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:9px;">
        <div style="display:flex;align-items:center;gap:7px;">
          <span style="font-size:15px;">🎯</span>
          <span style="font-size:12px;font-weight:600;color:var(--ink);">Бүгінгі мақсат</span>
        </div>
        <div style="font-size:11.5px;color:color-mix(in srgb,var(--ink) 60%,transparent);">${statusLine}</div>
      </div>
      <div style="height:8px;background:color-mix(in srgb,var(--ink) 6%,transparent);border-radius:4px;overflow:hidden;">
        <div style="height:100%;width:${pct}%;background:${barColor};border-radius:4px;transition:width .4s ease;"></div>
      </div>
    </div>`;
}

// Stage 7.6 — Жан дос ұсынысы (тек тегін user-ге, Бүгін бетінде)
function renderJandosUpsell(data) {
  const el = $('[data-field="jandos-upsell"]', root());
  if (!el) return;
  const sub = data?.user?.subscription_status || '';
  const premium = (sub === 'jandos' || sub === 'family_member');
  if (premium) { el.style.display = 'none'; el.innerHTML = ''; return; }
  el.style.display = '';
  el.innerHTML = `
    <div data-action="navigate" data-target="jandos" style="margin:0 16px 20px;padding:18px;background:linear-gradient(155deg,#FBF7EF,#F1E9D8);border:.5px solid rgba(139,94,52,.25);border-radius:16px;cursor:pointer;box-shadow:0 4px 14px rgba(139,94,52,.06);">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <span style="font-size:18px;">🕯</span>
        <span style="font-family:'PT Serif',Georgia,serif;font-size:15px;font-weight:700;color:#1C1A16;">PARAQTA-ның Жан досы бол</span>
      </div>
      <div style="font-size:12px;color:rgba(28,26,22,.65);line-height:1.8;margin-bottom:13px;">
        ✓ Кітап ұсыныстары — талғамыңа сай<br>
        ✓ Достар рейтингі — бәсекелес<br>
        ✓ Айлық + жылдық есеп<br>
        ✓ Жылдық мақсат + толық статистика<br>
        ✓ Шексіз топтар + өз тобыңды құру
      </div>
      <button data-action="navigate" data-target="jandos" style="width:100%;padding:11px;background:#8B5E34;color:#fff;border:none;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer;font-family:Onest,sans-serif;">PARAQTA-ның Жан досы бол — 690₸/ай</button>
    </div>`;
}

// Stage 7.7 — Бонус квест: 7 күн ҚАТАРЫНАН оқы → 1 ай Жан дос тегін (free-only).
// Бөлек endpoint /api/bonus-quest (cache жоқ; 7-ге жетсе автоматты грант).
async function renderBonusQuest() {
  const el = $('[data-field="bonus-quest"]', root());
  if (!el) return;
  let q;
  try {
    q = await api('/api/bonus-quest');
  } catch (e) {
    el.style.display = 'none'; el.innerHTML = ''; return;
  }
  if (!q || !q.visible) { el.style.display = 'none'; el.innerHTML = ''; return; }
  el.style.display = '';

  const target = Number(q.target) || 7;
  const progress = Math.min(Number(q.progress) || 0, target);

  // 🎉 Жаңа ғана аяқталды — жеңіс
  if (q.status === 'just_completed') {
    el.innerHTML = `
      <div style="margin:14px 16px 0;padding:20px 18px;background:linear-gradient(155deg,#FBF7EF,#EDE0C8);border:.5px solid rgba(139,94,52,.3);border-radius:16px;text-align:center;box-shadow:0 6px 22px rgba(139,94,52,.14);">
        <div style="font-size:36px;margin-bottom:6px;">🎉</div>
        <div style="font-family:'PT Serif',Georgia,serif;font-size:18px;font-weight:700;color:#1C1A16;margin-bottom:5px;">Құттықтаймыз!</div>
        <div style="font-size:12.5px;color:rgba(28,26,22,.65);line-height:1.55;">7 күн қатарынан оқыдың — <b style="color:#8B5E34;">PARAQTA-ның Жан досы</b> болдың! 1 айға барлық мүмкіндік тегін 🕯</div>
      </div>`;
    toast('🎉 PARAQTA-ның Жан досы болдың — 1 айға тегін!');
    return;
  }

  // ✓ Бұрын аяқталған (грант берілген)
  if (q.status === 'completed') {
    el.innerHTML = `
      <div style="margin:14px 16px 0;padding:14px 16px;background:var(--card);border:.5px solid rgba(58,92,60,.3);border-radius:14px;display:flex;align-items:center;gap:12px;">
        <div style="font-size:22px;">🕯</div>
        <div style="flex:1;font-size:12.5px;color:#3A5C3C;font-weight:600;">PARAQTA-ның Жан досысың — 1 айға барлық мүмкіндік тегін</div>
      </div>`;
    return;
  }

  // ▶ Белсенді — прогресс
  const candles = Array.from({ length: target }, (_, i) => {
    const lit = i < progress;
    return `<span style="font-size:21px;line-height:1;${lit ? '' : 'opacity:.2;filter:grayscale(1);'}">🕯</span>`;
  }).join('');
  const pct = target > 0 ? Math.round((progress / target) * 100) : 0;
  const remaining = target - progress;
  const sub = progress === 0
    ? 'Бүгін осы сайтта оқып, бірінші шамыңды жақ! 🕯'
    : (remaining === 1
        ? 'Тек 1 күн қалды — жеңіске жақынсың!'
        : `Сайтта қатарынан оқы — тағы ${remaining} күн қалды`);

  el.innerHTML = `
    <div style="margin:14px 16px 0;padding:16px;background:linear-gradient(155deg,#FBF7EF,#F1E9D8);border:.5px solid rgba(139,94,52,.25);border-radius:16px;box-shadow:0 4px 14px rgba(139,94,52,.06);">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
        <span style="font-size:16px;">🎁</span>
        <span style="font-family:'PT Serif',Georgia,serif;font-size:14.5px;font-weight:700;color:#1C1A16;">Сайтта 7 күн оқы — 1 ай бәрі тегін</span>
      </div>
      <div style="font-size:11.5px;color:rgba(28,26,22,.6);margin-bottom:9px;">${sub}</div>
      <div style="font-size:10.5px;color:rgba(139,94,52,.85);background:rgba(139,94,52,.07);border-radius:8px;padding:7px 9px;line-height:1.5;margin-bottom:12px;">
        ℹ️ Бұл — <b>осы сайтта</b> оқыған күндерің. Күніне кемі бір рет оқу сессиясын жаса (кітап аш → оқы). Бұл серия <b>боттағы серияңнан бөлек</b> саналады.
      </div>
      <div style="display:flex;justify-content:space-between;gap:4px;margin-bottom:10px;">${candles}</div>
      <div style="height:7px;background:rgba(28,26,22,.07);border-radius:4px;overflow:hidden;">
        <div style="height:100%;width:${pct}%;background:linear-gradient(to right,#8B5E34,#C9A96E);border-radius:4px;transition:width .4s ease;"></div>
      </div>
      <div style="text-align:right;font-size:10.5px;color:rgba(28,26,22,.5);margin-top:6px;">${progress}/${target} күн · сайтта қатарынан</div>
    </div>`;
}

// ❗22.09: «Қош келдің» ТЕРЕЗЕСІ ӨШІРІЛДІ.
// Ол кезекке тіркелмейтін, әрі фондағы `/api/today/v2` жүктелгенде
// шығатындықтан адам ҚАЙ БЕТТЕ тұрса, соның үстіне жабысатын (тестте
// Рейтинг пен Бірге беттерінің үстінен шықты). Айтатын хабары «Сайтта
// 7 күн оқы» карточкасында бұрыннан тұр — қайталаудың қажеті жоқ.

// ── Main entry ─────────────────────────────────────────────────
let _loading = false;
export async function loadTodayV2() {
  if (_loading) return;
  _loading = true;
  try {
    const data = await api('/api/today/v2');
    state.data = data;
    state.activeBookIdx = 0;

    applyVisibility(data);
    renderUser(data);
    renderStreakHero(data);
    // Стрик-мұздату: кеше өткізіп қайта оралса — «стрик қорғалды» мерекесі
    if (data.streak_froze) {
      import('../celebrations/index.js')
        .then(m => m.celebrateStreakSaved(data.streak_froze, Number((data.user || {}).streak || 0)))
        .catch(() => {});
    }
    // Бот-байланыс: Telegram push арнасын ашу (жетпейтіндерге ғана баннер)
    import('../bot-connect/index.js').then(m => m.initBotConnect()).catch(() => {});
    renderMonthlyWrappedBanner(data);
    // Stage 8: Абаймен 45 күн челленджі (активация hero)
    import('../abai/index.js').then(m => m.renderAbaiCard()).catch(() => {});
    renderDailyGoal(data); // Stage 7.6: күндік мақсат виджеті
    // Stage 7.1: renderReadingNow() → Stage 7.2 (Оқу беті). Қазір skip.
    renderStats(data);
    // Stage 7.6: Бүгіндегі 15-күндік heatmap алынды (профильдегі "Оқу шамы" — қайталау).
    renderTodaySessions(data); // Stage 7.2.2: бүгінгі сессиялар
    renderRankWidget(data);    // Stage 7.1: айлық рейтинг
    renderFamily(data);
    renderGroups(data);
    renderJandosUpsell(data);  // Stage 7.6: тегін user-ге Жан дос ұсынысы
    // Stage 7.7: бонус квест (тек free user-ге; бөлек endpoint)
    const _sub = data?.user?.subscription_status || 'free';
    if (_sub === 'free') {
      renderBonusQuest();
    } else {
      const _bq = $('[data-field="bonus-quest"]', root());
      if (_bq) { _bq.style.display = 'none'; _bq.innerHTML = ''; }
    }
    // renderQuote/renderFeatured алынды (Бүгін бетін жеңілдету)
    renderMarathon(data);

    // 🎉 Мереке: күндік мақсат (стрик енді сессия біткенде — oqu). localStorage анти-қайталау.
    import('../celebrations/index.js').then(m => {
      m.maybeCelebrateDailyGoal(data.daily_goal);
    }).catch(() => {});

    // Global nav streak (бар app-nav)
    const navStreak = document.getElementById('navStreak');
    if (navStreak && data.user?.streak) {
      navStreak.textContent = `🔥 ${data.user.streak} күн`;
    }
    const navName = document.getElementById('navName');
    if (navName && data.user?.name) navName.textContent = data.user.name;
    const navAv = document.getElementById('navAv');
    if (navAv && data.user?.initials) navAv.textContent = data.user.initials;

    updatePomoUI();
    // 22.09: бұл жерде «Қош келдің» терезесі шақырылатын — өшірілді.
  } catch (e) {
    console.error('loadTodayV2 failed', e);
    toast('Бүгін бетінің деректері жүктелмеді');
  } finally {
    _loading = false;
  }
}
